import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom, timeout } from 'rxjs';
import { AnalyticsService, SKIP_ERROR_TRACKING_STATUSES } from '@lite-workspace/shared/services/analytics';
import { SUPPORT_TELEGRAM_BOT_TG_SCHEME_URL, SUPPORT_TELEGRAM_BOT_URL, SUPPORT_VK_CHAT_URL } from '@lite-workspace/dto-common';

export type SupportLinkChannel = 'telegram' | 'vk';

const FALLBACK_URL: Readonly<Record<SupportLinkChannel, string>> = {
  telegram: SUPPORT_TELEGRAM_BOT_URL,
  vk: SUPPORT_VK_CHAT_URL,
};

const DEEPLINK_ENDPOINT = '/api/support/deeplink';

// Штатные отказы минта: гость без сессии и неподтверждённый токен. 5xx сверх них остаются сбоем.
const EXPECTED_FAILURE_STATUSES: readonly number[] = [401, 503];

// Пока минт не ответил, пользователь смотрит на пустую вкладку, а переход в `tg://` теряет жест.
export const SUPPORT_DEEPLINK_TIMEOUT_MS = 3000;

export const TG_SUPPORT_LINK = SUPPORT_TELEGRAM_BOT_URL;

// Минт живёт 10 минут и одноразовый: заготовку старше этого срока переход не берёт.
const PREFETCHED_LINK_TTL_MS = 9 * 60_000;

const TELEGRAM_WEB_HOST = 't.me';
const TELEGRAM_BOT_USERNAME_PATTERN = /^[A-Za-z0-9_]{5,32}$/;

// A gateway without `channel` support mints a Telegram link for a VK request; only a VK host may open from the VK row.
const VK_HOSTS: ReadonlySet<string> = new Set(['vk.me', 'vk.ru', 'vk.com']);

function isVkUrl(url: string): boolean {
  try {
    return VK_HOSTS.has(new URL(url).hostname);
  } catch {
    return false;
  }
}

interface SupportDeeplinkResponse {
  url?: string;
}

@Injectable({ providedIn: 'root' })
export class SupportDeeplinkService {
  private readonly http = inject(HttpClient);
  private readonly analytics = inject(AnalyticsService, { optional: true });
  private prefetched: { url: string; at: number } | null = null;
  private prefetching = false;
  // Separate slot: the code is one-time and each channel holds its own.
  private prefetchedVk: { url: string; at: number } | null = null;
  private prefetchingVk = false;

  // Любой отказ — 401 на экране входа, 503, сеть — и пустой 204 выключенного хелпдеска деградируют до голой ссылки
  // на бота: кнопка поддержки обязана открываться всегда. Ответ не кэшируется: токен одноразовый и живёт 10 минут,
  // кэшированный отдал бы уже погашенный.
  async supportUrl(channel: SupportLinkChannel = 'telegram'): Promise<string> {
    const openpanelSessionId: string | undefined = this.analytics?.getSessionId();
    try {
      const response: SupportDeeplinkResponse | null = await firstValueFrom(
        this.http
          .post<SupportDeeplinkResponse | null>(
            DEEPLINK_ENDPOINT,
            { ...(openpanelSessionId ? { openpanelSessionId } : {}), ...(channel === 'vk' ? { channel } : {}) },
            { context: new HttpContext().set(SKIP_ERROR_TRACKING_STATUSES, EXPECTED_FAILURE_STATUSES) },
          )
          .pipe(timeout(SUPPORT_DEEPLINK_TIMEOUT_MS)),
      );
      const url: string | undefined = response?.url;
      return url && (channel !== 'vk' || isVkUrl(url)) ? url : FALLBACK_URL[channel];
    } catch {
      return FALLBACK_URL[channel];
    }
  }

  /**
   * Минт до клика — на открытии меню или карточки поддержки: переход в `tg://` идёт синхронно в обработчике
   * клика, ждать ответа ему нельзя. Свежая заготовка или идущий минт второго запроса не делают.
   */
  prefetchAppUrl(): void {
    if (this.prefetching || this.freshPrefetched()) return;
    this.prefetching = true;
    void this.supportAppUrl()
      .then(url => (this.prefetched = { url, at: Date.now() }))
      .finally(() => (this.prefetching = false));
  }

  /** Адрес для перехода прямо сейчас: заготовка забирается (токен одноразовый); нет свежей — бот без токена. */
  takeAppUrl(): string {
    const url: string | null = this.freshPrefetched();
    this.prefetched = null;
    return url ?? SUPPORT_TELEGRAM_BOT_TG_SCHEME_URL;
  }

  private freshPrefetched(): string | null {
    return this.prefetched && Date.now() - this.prefetched.at < PREFETCHED_LINK_TTL_MS ? this.prefetched.url : null;
  }

  /** Как `prefetchAppUrl`, для ВКонтакте: окно открывается синхронно на клике, ждать ответа нельзя. */
  prefetchVkUrl(): void {
    if (this.prefetchingVk || this.freshPrefetchedVk()) return;
    this.prefetchingVk = true;
    void this.supportUrl('vk')
      .then(url => (this.prefetchedVk = { url, at: Date.now() }))
      .finally(() => (this.prefetchingVk = false));
  }

  takeVkUrl(): string {
    const url: string | null = this.freshPrefetchedVk();
    this.prefetchedVk = null;
    return url ?? SUPPORT_VK_CHAT_URL;
  }

  private freshPrefetchedVk(): string | null {
    return this.prefetchedVk && Date.now() - this.prefetchedVk.at < PREFETCHED_LINK_TTL_MS ? this.prefetchedVk.url : null;
  }

  /** The same link as a `tg://` address: the bot comes from the minted link, `start` is encoded as t.me itself does. */
  async supportAppUrl(): Promise<string> {
    const url: string = await this.supportUrl();
    try {
      const parsed = new URL(url);
      const bot: string = parsed.pathname.replace(/^\/+/, '');
      if (parsed.hostname !== TELEGRAM_WEB_HOST || !TELEGRAM_BOT_USERNAME_PATTERN.test(bot)) {
        return SUPPORT_TELEGRAM_BOT_TG_SCHEME_URL;
      }

      const start: string | null = parsed.searchParams.get('start');
      return `tg://resolve?domain=${bot}${start ? `&start=${encodeURIComponent(start)}` : ''}`;
    } catch {
      return SUPPORT_TELEGRAM_BOT_TG_SCHEME_URL;
    }
  }
}
